// @ts-nocheck
import { useState, useEffect, useCallback, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { getSessionUser } from '@/lib/authSession';
import {
  EventData,
  DEFAULT_EVENT,
  Venue,
  Sport,
  EventType,
  EventCategory,
  Sponsor,
  EventSponsor,
  PricingTier,
  EventExtra,
  TicketOption,
  EventFaq,
  WaiverSignature,
  DiscountPolicy,
  EventDivisionSettings,
  createDefaultPricingTiers,
} from "@/types/events";

const RECRUITLOOK_EVENTS_ORG_ID = "4a900125-64a5-4a26-b5f6-860fd278a186";

interface UseEventEditorOptions {
  /** Called after a new event is created so the page can update the URL */
  onCreated?: (newId: string) => void;
  organizationId?: string;
}

export function useEventEditor(
  eventId: string | undefined,
  options?: UseEventEditorOptions
) {
  const [event, setEvent] = useState<EventData>(DEFAULT_EVENT);
  const eventRef = useRef<EventData>(DEFAULT_EVENT);
  const [venues, setVenues] = useState<Venue[]>([]);
  const [sports, setSports] = useState<Sport[]>([]);
  const [eventTypes, setEventTypes] = useState<EventType[]>([]);
  const [categories, setCategories] = useState<EventCategory[]>([]);
  const [sponsors, setSponsors] = useState<Sponsor[]>([]);
  const [eventSponsors, setEventSponsorsState] = useState<EventSponsor[]>([]);
  const [pricingTiers, setPricingTiers] = useState<PricingTier[]>([]);
  const [extras, setExtras] = useState<EventExtra[]>([]);
  const [tickets, setTickets] = useState<TicketOption[]>([]);
  const [faqs, setFaqs] = useState<EventFaq[]>([]);
  const [waiverSignatures, setWaiverSignatures] = useState<WaiverSignature[]>(
    []
  );
  const [discountPolicies, setDiscountPolicies] = useState<DiscountPolicy[]>(
    []
  );
  const [divisionSettings, setDivisionSettings] = useState<EventDivisionSettings[]>([]);
  // AI-builder-provided divisions to write into the event_divisions table on save.
  // Each item: { name: string; price_override?: number | null }
  const [divisions, setDivisions] = useState<any[]>([]);
  const divisionsWrittenRef = useRef(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);

  // Tracks the real persisted event ID (may start null for new events)
  const [persistedId, setPersistedId] = useState<string | null>(
    eventId && eventId !== "new" ? eventId : null
  );
  // Ref mirror of persistedId so concurrent saves see the latest value synchronously
  const persistedIdRef = useRef<string | null>(
    eventId && eventId !== "new" ? eventId : null
  );

  const initialEventRef = useRef<string>("");
  const userHasEdited = useRef(false);
  const autosaveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isMounted = useRef(true);
  // Guard: prevent concurrent create/save calls
  const isCreating = useRef(false);
  const isSavingRef = useRef(false);
  const pendingSaveRef = useRef(false);
  const savePromiseRef = useRef<Promise<void> | null>(null);
  const failedAutoSaveSnapshotRef = useRef<string | null>(null);
  const eventSponsorsTouchedRef = useRef(false);
  // Stable ref for options to avoid recreating save on every render
  const optionsRef = useRef(options);
  optionsRef.current = options;

  // Keep persistedId in sync when the parent passes a real eventId
  useEffect(() => {
    if (eventId && eventId !== "new" && eventId !== persistedId) {
      persistedIdRef.current = eventId;
      setPersistedId(eventId);
    }
  }, [eventId, persistedId]);

  // Load reference data
  useEffect(() => {
    async function loadReferenceData() {
      const [
        { data: sportsData },
        { data: eventTypesData },
        { data: categoriesData },
        { data: venuesData },
        { data: sponsorsData },
      ] = await Promise.all([
        supabase.from("sports").select("id, name, slug").order("name"),
        supabase.from("event_types").select("id, name").order("name"),
        supabase.from("event_categories").select("id, name").order("name"),
        supabase.from("venues").select("*").eq("is_active", true).order("name"),
        supabase.from("sponsors").select("*").order("name"),
      ]);
      setSports(sportsData ?? []);
      setEventTypes(eventTypesData ?? []);
      setCategories(categoriesData ?? []);
      setVenues(venuesData ?? []);
      setSponsors(sponsorsData ?? []);
    }
    loadReferenceData();
  }, []);

  // Discount policies are per-organization. Scope the wizard dropdown to the
  // event's organization so it matches the org-scoped Discount Policies page
  // (rather than showing every org's policies from a global list).
  const policyOrgId = event.organization_id || options?.organizationId;
  useEffect(() => {
    async function loadDiscountPolicies() {
      if (!policyOrgId) {
        setDiscountPolicies([]);
        return;
      }
      const { data } = await supabase
        .from("event_discount_policies")
        .select("id, name, description")
        .eq("is_active", true)
        .eq("organization_id", policyOrgId)
        .order("name");
      setDiscountPolicies(data ?? []);
    }
    loadDiscountPolicies();
  }, [policyOrgId]);


  // Load event data if editing existing event
  useEffect(() => {
    const idToLoad = eventId && eventId !== "new" ? eventId : null;
    if (!idToLoad) {
      setPricingTiers(createDefaultPricingTiers());
      setLoading(false);
      return;
    }

    async function loadEvent() {
      setLoading(true);
      const { data: eventData } = await supabase
        .from("events")
        .select("*")
        .eq("id", idToLoad!)
        .single();

      if (eventData) {
        const mapped: EventData = {
          ...DEFAULT_EVENT,
          ...eventData,
          event_category_ids: eventData.event_category_ids ?? [],
          additional_venue_ids: eventData.additional_venue_ids ?? [],
          gallery_images: eventData.gallery_images ?? [],
          registration_alert_emails: eventData.registration_alert_emails ?? [],
          payment_notification_emails:
            eventData.payment_notification_emails ?? [],
          roster_upload_emails: eventData.roster_upload_emails ?? [],
          selected_sponsor_ids: eventData.selected_sponsor_ids ?? [],
        };
        // Per-venue details (event_venue_info) ride along on the event state so
        // edits mark the event dirty; saveRelated writes them back.
        try {
          const { data: vi } = await (supabase as any)
            .from("event_venue_info")
            .select("venue_id, divisions, doors_open, notes, sort_order")
            .eq("event_id", idToLoad!)
            .order("sort_order");
          (mapped as any)._venue_info = vi || [];
        } catch {
          (mapped as any)._venue_info = [];
        }
        eventRef.current = mapped;
        setEvent(mapped);
        initialEventRef.current = JSON.stringify(mapped);
      }

      const [
        { data: eventSponsorsData },
        { data: pricingData },
        { data: extrasData },
        { data: ticketsData },
        { data: faqsData },
        { data: signaturesData },
        { data: divisionSettingsData },
      ] = await Promise.all([
        supabase
          .from("event_sponsors")
          .select("*")
          .eq("event_id", idToLoad!)
          .order("display_order"),
        supabase
          .from("event_pricing")
          .select("*")
          .eq("event_id", idToLoad!),
        supabase
          .from("event_extras")
          .select("*")
          .eq("event_id", idToLoad!)
          .order("sort_order"),
        supabase
          .from("event_ticket_options")
          .select("*")
          .eq("event_id", idToLoad!)
          .order("sort_order"),
        supabase
          .from("event_faqs")
          .select("*")
          .eq("event_id", idToLoad!)
          .order("sort_order"),
        supabase
          .from("event_waiver_signatures")
          .select("*")
          .eq("event_id", idToLoad!)
          .order("signed_at", { ascending: false }),
        supabase
          .from("event_division_settings")
          .select("*")
          .eq("event_id", idToLoad!),
      ]);

      setDivisionSettings(divisionSettingsData ?? []);

      setEventSponsorsState(eventSponsorsData ?? []);

      let loadedTiers: PricingTier[] = [];
      if (pricingData?.[0]?.pricing_structure) {
        const raw = pricingData[0].pricing_structure;
        loadedTiers = Array.isArray(raw) ? raw : [];
      }
      if (loadedTiers.length === 0) {
        loadedTiers = createDefaultPricingTiers();
      }
      setPricingTiers(loadedTiers);
      setExtras(extrasData ?? []);
      setTickets(ticketsData ?? []);
      setFaqs(faqsData ?? []);
      setWaiverSignatures(signaturesData ?? []);

      setLoading(false);
    }
    loadEvent();
  }, [eventId]);

  // Track dirty state
  const updateEvent = useCallback((updates: Partial<EventData>) => {
    const next = { ...eventRef.current, ...updates };
    eventRef.current = next;
    setEvent(next);
    setDirty(true);
    userHasEdited.current = true;
  }, []);

  const markDirtyFromRelated = useCallback(() => {
    setDirty(true);
    userHasEdited.current = true;
  }, []);

  const setEventSponsors = useCallback((value: any) => {
    eventSponsorsTouchedRef.current = true;
    setEventSponsorsState(value);
  }, []);

  // Build the event fields payload shared between INSERT and UPDATE
  const buildEventPayload = useCallback(() => {
    const event = eventRef.current;
    const sportId =
      event.sport_id ||
      sports.find(
        (s: any) =>
          s.name?.toLowerCase() === String((event as any).sport ?? "").toLowerCase() ||
          s.name?.toLowerCase().includes(String((event as any).sport ?? "").toLowerCase())
      )?.id ||
      null;
    const mainVenueId = event.main_venue_id || (event as any).venue?.id || (event as any).venue_id || null;
    const mainVenue = venues.find((v: any) => v.id === mainVenueId) || (event as any).venue || null;
    const categoryIds = Array.isArray(event.event_category_ids)
      ? event.event_category_ids
      : Array.isArray((event as any).categories)
        ? (event as any).categories.map((c: any) => (typeof c === "string" ? c : c?.id)).filter(Boolean)
        : Array.isArray((event as any).category_ids)
          ? (event as any).category_ids
          : [];

    return {
      name: event.name,
      description: event.description,
      gender: event.gender,
      sport_id: sportId,
      event_type_id: event.event_type_id || null,
      event_category_ids: categoryIds,
      start_date: event.start_date || null,
      end_date: event.end_date || null,
      registration_open_date: event.registration_open_date || null,
      registration_close_date: event.registration_close_date || null,
      more_info: event.more_info,
      thumbnail_url: event.thumbnail_url,
      cover_photo_url: (event as any).cover_photo_url ?? null,
      main_venue_id: mainVenueId,
      venue_name: mainVenue?.name ?? (event as any).venue_name ?? null,
      venue_city: mainVenue?.city ?? (event as any).venue_city ?? null,
      venue_state: mainVenue?.state ?? (event as any).venue_state ?? null,
      additional_venue_ids: event.additional_venue_ids,
      accept_online_registrations: event.accept_online_registrations,
      allow_checkin: event.allow_checkin,
      allow_coupons: event.allow_coupons,
      discount_policy_id: event.discount_policy_id || null,
      external_registration_url: event.external_registration_url,
      registration_type: event.registration_type,
      roster_deadline: event.roster_deadline || null,
      roster_minimum_players:
        (event as any).roster_minimum_players == null ||
        (event as any).roster_minimum_players === ""
          ? 6
          : Number((event as any).roster_minimum_players),
      waitlist_enabled: event.waitlist_enabled,
      circuit_event_enabled: (event as any).circuit_event_enabled ?? false,
      circuit_step_answered: (event as any).circuit_step_answered ?? false,
      banners_not_needed: (event as any).banners_not_needed ?? false,

      waitlist_message: event.waitlist_message,
      accept_special_requests: event.accept_special_requests,
      special_request_instructions: event.special_request_instructions,
      default_special_request_fee: event.default_special_request_fee,
      event_format: event.event_format,
      checkin_instructions: event.checkin_instructions,
      rules: event.rules,
      schedule_info: event.schedule_info,
      pricing_notes: event.pricing_notes,
      age_groups_description: event.age_groups_description,
      background_image_url: event.background_image_url,
      hotel_booking_url: event.hotel_booking_url,
      stay_to_play_enabled: event.stay_to_play_enabled,
      stay_to_play_mode: event.stay_to_play_mode,
      stay_to_play_required: event.stay_to_play_required,
      stay_to_play_notes: event.stay_to_play_notes,
      hotel_exempt_radius_miles: event.hotel_exempt_radius_miles ?? null,
      promo_video_url: event.promo_video_url,
      live_streaming_description: event.live_streaming_description,
      live_streaming_url: event.live_streaming_url,
      gallery_images: event.gallery_images,
      ticket_display_text: event.ticket_display_text,
      enable_media_credentials: event.enable_media_credentials,
      media_credentials_price: event.media_credentials_price,
      media_credentials_description: event.media_credentials_description,
      media_credentials_form_id: event.media_credentials_form_id || null,
      media_credentials_notification_email:
        event.media_credentials_notification_email,
      media_credentials_enabled: (event as any).media_credentials_enabled ?? false,
      media_credentials_paid: (event as any).media_credentials_paid ?? false,
      enable_coach_info: event.enable_coach_info,
      coach_info_description: event.coach_info_description,
      coach_info_form_id: event.coach_info_form_id || null,
      coach_notification_email: event.coach_notification_email,
      coaches_external_url: event.coaches_external_url,
      coach_attendance_enabled: (event as any).coach_attendance_enabled ?? false,
      coach_attendance_paid: (event as any).coach_attendance_paid ?? true,
      is_ncaa_live_event: (event as any).is_ncaa_live_event ?? false,
      show_coaches_publicly: (event as any).show_coaches_publicly ?? true,
      registration_alert_emails: event.registration_alert_emails,
      payment_notification_emails: event.payment_notification_emails,
      roster_upload_emails: event.roster_upload_emails,
      waiver_required_at_registration: event.waiver_required_at_registration,
      waiver_text: event.waiver_text,
      waiver_url: event.waiver_url,
      waiver_version: event.waiver_version,
      selected_sponsor_ids: event.selected_sponsor_ids,
      checkin_short_description: (event as any).checkin_short_description ?? null,
      tickets_short_description: (event as any).tickets_short_description ?? null,
      extras_short_description: (event as any).extras_short_description ?? null,
      schedule_requests_short_description: (event as any).schedule_requests_short_description ?? null,
      hotels_short_description: (event as any).hotels_short_description ?? null,
      live_stream_short_description: (event as any).live_stream_short_description ?? null,
      rules_short_description: (event as any).rules_short_description ?? null,
      media_credentials_short_description: (event as any).media_credentials_short_description ?? null,
      coaches_short_description: (event as any).coaches_short_description ?? null,
      registration_levels_enabled: (event as any).registration_levels_enabled ?? false,
      registration_levels_required: (event as any).registration_levels_required ?? false,
      registration_levels_help_text: (event as any).registration_levels_help_text ?? null,
      // Scheduling tiers (D33)
      scheduling_tiers_enabled: (event as any).scheduling_tiers_enabled ?? false,
      priority_tier_price: (event as any).priority_tier_price ?? 0,
      club_priority_tier_price: (event as any).club_priority_tier_price ?? 0,
      club_priority_min_teams: (event as any).club_priority_min_teams ?? 5,
      showcase_tier_enabled: (event as any).showcase_tier_enabled ?? false,
      showcase_tier_price: (event as any).showcase_tier_price ?? 0,
      // Scheduling tier names / descriptions / perks / fee-waiving
      priority_tier_name: (event as any).priority_tier_name ?? null,
      club_priority_tier_name: (event as any).club_priority_tier_name ?? null,
      showcase_tier_name: (event as any).showcase_tier_name ?? null,
      individual_request_name: (event as any).individual_request_name ?? null,
      priority_tier_description: (event as any).priority_tier_description ?? null,
      club_priority_tier_description: (event as any).club_priority_tier_description ?? null,
      showcase_tier_description: (event as any).showcase_tier_description ?? null,
      individual_request_description: (event as any).individual_request_description ?? null,
      priority_tier_perks: (event as any).priority_tier_perks ?? null,
      club_priority_tier_perks: (event as any).club_priority_tier_perks ?? null,
      showcase_tier_perks: (event as any).showcase_tier_perks ?? null,
      priority_tier_waives_fees: (event as any).priority_tier_waives_fees ?? true,
      club_priority_tier_waives_fees: (event as any).club_priority_tier_waives_fees ?? true,
      showcase_tier_waives_fees: (event as any).showcase_tier_waives_fees ?? true,
      individual_request_enabled: (event as any).individual_request_enabled ?? false,
      individual_request_price: (event as any).individual_request_price ?? 0,
      allow_pay_later: (event as any).allow_pay_later ?? false,
      pay_later_methods: (event as any).pay_later_methods ?? null,
      pay_later_instructions: (event as any).pay_later_instructions ?? null,
      pay_later_check_instructions: (event as any).pay_later_check_instructions ?? null,
      pay_later_venmo_note: (event as any).pay_later_venmo_note ?? null,
      pay_later_venmo_handle: (event as any).pay_later_venmo_handle ?? null,

      pay_later_due_days_before_event: (event as any).pay_later_due_days_before_event ?? null,
      pay_later_late_fee: (event as any).pay_later_late_fee ?? null,
      pay_later_block_roster_until_paid: (event as any).pay_later_block_roster_until_paid ?? false,
    };
  }, [sports, venues]);

  // Save related tables (pricing, extras, tickets, faqs, sponsors)
  const saveRelated = useCallback(
    async (targetId: string) => {
      // Pricing
      const { error: pricingError } = await supabase
        .from("event_pricing")
        .upsert(
          {
            event_id: targetId,
            pricing_structure: pricingTiers,
            updated_at: new Date().toISOString(),
          },
          { onConflict: "event_id" }
        );
      if (pricingError) throw pricingError;

      // Venue details (event_venue_info): one row per selected venue.
      const venueInfo = (eventRef.current as any)._venue_info;
      if (Array.isArray(venueInfo)) {
        const ev: any = eventRef.current;
        const selected = [ev.main_venue_id, ...(ev.additional_venue_ids || [])]
          .filter(Boolean)
          .filter((id: string, i: number, a: string[]) => a.indexOf(id) === i);
        const byId = new Map(venueInfo.map((r: any) => [r.venue_id, r]));
        if (selected.length > 0) {
          const rows = selected.map((venueId: string, i: number) => {
            const r: any = byId.get(venueId) || {};
            return {
              event_id: targetId,
              venue_id: venueId,
              divisions: r.divisions ?? "",
              doors_open: r.doors_open ?? "",
              notes: r.notes ?? "",
              sort_order: i,
            };
          });
          const { error: viError } = await (supabase as any)
            .from("event_venue_info")
            .upsert(rows, { onConflict: "event_id,venue_id" });
          if (viError) throw viError;
        }
        let del = (supabase as any).from("event_venue_info").delete().eq("event_id", targetId);
        if (selected.length > 0) del = del.not("venue_id", "in", `(${selected.join(",")})`);
        const { error: viDelError } = await del;
        if (viDelError) throw viDelError;
      }

      // Extras
      await supabase.from("event_extras").delete().eq("event_id", targetId);
      if (extras.length > 0) {
        const { error: extrasError } = await supabase
          .from("event_extras")
          .insert(
            extras.map((e, i) => ({
              event_id: targetId,
              name: e.name,
              price: e.price,
              description: e.description,
              max_quantity_per_registration: e.max_quantity_per_registration,
              sort_order: i,
              is_active: e.is_active,
              level: (e as any).level ?? "team",
              requires_team: e.requires_team ?? false,
              requires_player:
                ((e as any).level ?? "team") === "player" && (e.requires_player ?? false),
              requires_game_date: e.requires_game_date ?? false,
            }))
          );
        if (extrasError) throw extrasError;
      }

      // Tickets
      await supabase
        .from("event_ticket_options")
        .delete()
        .eq("event_id", targetId);
      if (tickets.length > 0) {
        const { error: ticketsError } = await supabase
          .from("event_ticket_options")
          .insert(
            tickets.map((t, i) => ({
              event_id: targetId,
              name: t.name,
              price: t.price,
              description: t.description,
              sort_order: i,
              is_active: t.is_active,
            }))
          );
        if (ticketsError) throw ticketsError;
      }

      // FAQs
      await supabase.from("event_faqs").delete().eq("event_id", targetId);
      if (faqs.length > 0) {
        const { error: faqsError } = await supabase
          .from("event_faqs")
          .insert(
            faqs.map((f, i) => ({
              event_id: targetId,
              question: f.question,
              answer: f.answer,
              category: f.category,
              sort_order: i,
              is_active: f.is_active,
            }))
          );
        if (faqsError) throw faqsError;
      }

      // Sponsors
      if (eventSponsorsTouchedRef.current) {
        await supabase
          .from("event_sponsors")
          .delete()
          .eq("event_id", targetId);
      }
      if (eventSponsorsTouchedRef.current && eventSponsors.length > 0) {
        const { error: sponsorsError } = await supabase
          .from("event_sponsors")
          .insert(
            eventSponsors.map((s, i) => ({
              event_id: targetId,
              sponsor_id: s.sponsor_id,
              display_order: i,
            }))
          );
        if (sponsorsError) throw sponsorsError;
      }
      // Division settings (relational replacement for jsonb pricing)
      if (divisionSettings.length > 0) {
        const rows = divisionSettings.map((d) => ({
          event_id: targetId,
          catalog_division_id: d.catalog_division_id,
          has_boys: d.has_boys,
          has_girls: d.has_girls,
          boys_price: d.boys_price ?? 0,
          girls_price: d.girls_price ?? 0,
          boys_max_teams: d.boys_max_teams ?? null,
          girls_max_teams: d.girls_max_teams ?? null,
          accepts_special_requests: d.accepts_special_requests ?? false,
          special_request_notes: d.special_request_notes ?? "",
          special_request_fee: d.special_request_fee ?? 0,
          is_active: d.is_active,
          display_order: d.display_order ?? 0,
          updated_at: new Date().toISOString(),
        }));
        const { error: dsError } = await supabase
          .from("event_division_settings")
          .upsert(rows, { onConflict: "event_id,catalog_division_id" });
        if (dsError) throw dsError;
      }

      // Divisions (event_divisions) — created from the AI builder. Written once.
      if (divisions.length > 0 && !divisionsWrittenRef.current) {
        await supabase.from("event_divisions").delete().eq("event_id", targetId);
        const divRows = divisions.map((d, i) => ({
          event_id: targetId,
          name: d.name,
          // null price_override means the division uses the event's base price.
          price_override:
            d.price_override != null && !Number.isNaN(Number(d.price_override))
              ? Number(d.price_override)
              : null,
          sort_order: i,
          is_active: true,
        }));
        const { error: divError } = await supabase
          .from("event_divisions")
          .insert(divRows);
        if (divError) throw divError;
        divisionsWrittenRef.current = true;
      }
    },
    [pricingTiers, extras, tickets, faqs, eventSponsors, divisionSettings, divisions]
  );

  // Main save function — handles both create and update
  const save = useCallback(async () => {
    // Prevent concurrent saves of any kind
    if (isCreating.current || isSavingRef.current) {
      pendingSaveRef.current = true;
      return savePromiseRef.current ?? Promise.resolve();
    }
    isSavingRef.current = true;
    pendingSaveRef.current = false;

    const runSave = async () => {
      setSaving(true);
      setSaveError(null);

      try {
        let targetId = persistedIdRef.current;

        if (!targetId) {
          // ── CREATE: insert new event row ──
          // Set guard SYNCHRONOUSLY before any await to prevent concurrent inserts
          isCreating.current = true;

          const {
            data: { user },
          } = await getSessionUser();

          // Re-check after await — another save may have completed the create
          if (persistedIdRef.current) {
            targetId = persistedIdRef.current;
            isCreating.current = false;
          } else {
            const payload = {
              ...buildEventPayload(),
              status: "draft",
              is_active: true,
              organization_id: optionsRef.current?.organizationId || RECRUITLOOK_EVENTS_ORG_ID,
              created_by: user?.id || null,
            };

            const { data: created, error: createError } = await supabase
              .from("events")
              .insert(payload)
              .select("id")
              .single();

            if (createError) throw createError;
            if (!created) throw new Error("No data returned from insert");

            targetId = created.id;
            persistedIdRef.current = targetId;
            setPersistedId(targetId);
            isCreating.current = false;

            // Notify caller so it can update the URL
            optionsRef.current?.onCreated?.(targetId);
          }
        }

        if (!targetId) throw new Error("No target event id");

        // ── UPDATE always runs after create or for existing events ──
        const { error: eventError } = await supabase
          .from("events")
          .update(buildEventPayload())
          .eq("id", targetId);
        if (eventError) throw eventError;

        // Save related tables
        await saveRelated(targetId);

        setDirty(false);
        failedAutoSaveSnapshotRef.current = null;
        initialEventRef.current = JSON.stringify(eventRef.current);
      } catch (err: unknown) {
        isCreating.current = false;
        const message = err instanceof Error ? err.message : "Save failed";
        setSaveError(message);
        throw err;
      } finally {
        isSavingRef.current = false;
        savePromiseRef.current = null;
        setSaving(false);

        if (pendingSaveRef.current && isMounted.current) {
          pendingSaveRef.current = false;
          save().catch(() => {});
        }
      }
    };

    savePromiseRef.current = runSave();
    return savePromiseRef.current;
  }, [event, pricingTiers, extras, tickets, faqs, eventSponsors, divisionSettings, buildEventPayload, saveRelated]);

  // Validate required fields
  useEffect(() => {
    const errors: string[] = [];
    if (!event.name.trim()) errors.push("Event title");
    setValidationErrors(errors);
  }, [event.name]);

  // Autosave: debounce 1.5s after user-triggered changes
  useEffect(() => {
    if (!dirty || !userHasEdited.current || saving || isCreating.current)
      return;

    const autosaveSnapshot = JSON.stringify({
      event,
      pricingTiers,
      extras,
      tickets,
      faqs,
      eventSponsors,
      divisionSettings,
    });
    if (failedAutoSaveSnapshotRef.current === autosaveSnapshot) return;

    if (autosaveTimer.current) clearTimeout(autosaveTimer.current);

    autosaveTimer.current = setTimeout(async () => {
      if (validationErrors.length > 0) return;
      try {
        await save();
        if (isMounted.current) {
          setLastSavedAt(new Date());
        }
      } catch {
        failedAutoSaveSnapshotRef.current = autosaveSnapshot;
        // saveError already set
      }
    }, 1500);

    return () => {
      if (autosaveTimer.current) clearTimeout(autosaveTimer.current);
    };
  }, [dirty, saving, event, pricingTiers, extras, tickets, faqs, eventSponsors, divisionSettings, validationErrors, save]);

  // Fire save on unmount if dirty
  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
      if (autosaveTimer.current) clearTimeout(autosaveTimer.current);
      if (dirty && userHasEdited.current && validationErrors.length === 0) {
        save().catch(() => {});
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    event,
    updateEvent,
    markDirtyFromRelated,
    venues,
    setVenues,
    sports,
    eventTypes,
    categories,
    sponsors,
    setSponsors,
    eventSponsors,
    setEventSponsors,
    pricingTiers,
    setPricingTiers,
    extras,
    setExtras,
    tickets,
    setTickets,
    faqs,
    setFaqs,
    waiverSignatures,
    setWaiverSignatures,
    discountPolicies,
    divisionSettings,
    setDivisionSettings,
    divisions,
    setDivisions,
    loading,
    saving,
    dirty,
    saveError,
    save,
    lastSavedAt,
    validationErrors,
    /** The real persisted event ID (null until first save for new events) */
    persistedId,
  };
}